import { useEffect, useState, useCallback } from 'react'
import { Box } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useAuthIdentity, useServiceAuth } from '@/contexts/AuthContext'
import { ProjectDashboard, type ProjectFormData } from '@/components/project'
import { createProject, deleteProject, getProjects } from '@/services/projectService'
import type { Project } from '@/interfaces'

export function ProjectsPage() {
  const navigate = useNavigate()
  const { userId, organizationId } = useAuthIdentity()
  const getServiceAuth = useServiceAuth()

  // Note: using organizationId from AuthContext instead of useAccessControl
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [createProjectLoading, setCreateProjectLoading] = useState(false)
  const [createProjectError, setCreateProjectError] = useState<string | null>(null)

  // Load projects
  const loadProjects = useCallback(async () => {
    // Get fresh auth props - no re-renders on token changes
    const serviceAuth = getServiceAuth()
    if (!serviceAuth) {
      console.warn('ProjectsPage:loadProjects:serviceAuth unavailable (not signed in or no org)')
      return
    }

    try {
      setLoading(true)
      setError(null)
      console.debug('ProjectsPage:loadProjects:start', {
        userId,
        organizationId,
        serviceAuthUserId: serviceAuth.userId,
        serviceAuthOrgId: serviceAuth.orgId,
        serviceAuthOrgSlug: serviceAuth.orgSlug,
      })
      const projectsData = await getProjects(serviceAuth)
      setProjects(projectsData)
      console.debug('ProjectsPage:loadProjects:done', { count: projectsData.length })
    } catch (err) {
      console.error('ProjectsPage:loadProjects:error', err)
      setError(err instanceof Error ? err.message : 'Failed to load projects')
    } finally {
      setLoading(false)
    }
  }, [getServiceAuth])

  useEffect(() => {
    loadProjects()
  }, [userId, organizationId, loadProjects])

  const handleCreateProject = useCallback(
    async (formData: ProjectFormData) => {
      // Get fresh auth props - no re-renders on token changes
      const serviceAuth = getServiceAuth()
      if (!serviceAuth) {
        setCreateProjectError('Authentication required. Please sign in again.')
        return
      }

      try {
        setCreateProjectLoading(true)
        setCreateProjectError(null)

        const projectData = {
          name: formData.name,
          description: formData.description || null,
          organization_id: serviceAuth.orgId,
          user_id: serviceAuth.userId,
        }

        const newProject = await createProject(projectData, serviceAuth)

        // Add to projects list
        setProjects(prev => [newProject, ...prev])
      } catch (err) {
        setCreateProjectError(err instanceof Error ? err.message : 'Failed to create project')
      } finally {
        setCreateProjectLoading(false)
      }
    },
    [getServiceAuth]
  )

  const handleDeleteProject = useCallback(
    async (projectId: string) => {
      // Get fresh auth props - no re-renders on token changes
      const serviceAuth = getServiceAuth()
      if (!serviceAuth) return

      // Confirm deletion
      const project = projects.find(p => p.id === projectId)
      if (!project) return

      if (!window.confirm(`Are you sure you want to delete "${project.name}"?`)) {
        return
      }

      try {
        await deleteProject(projectId, serviceAuth)
        setProjects(prev => prev.filter(p => p.id !== projectId))
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to delete project')
      }
    },
    [getServiceAuth, projects]
  )

  const handleEditProject = (project: Project) => {
    // TODO: Implement project editing
    console.log('Edit project:', project)
  }

  const handleOpenProject = (projectId: string) => {
    navigate(`/workspace/${projectId}`)
  }

  return (
    <Box>
      <ProjectDashboard
        projects={projects}
        loading={loading}
        error={error}
        organizationId={organizationId || null}
        onRefresh={loadProjects}
        onCreateProject={handleCreateProject}
        onDeleteProject={handleDeleteProject}
        onEditProject={handleEditProject}
        onOpenProject={handleOpenProject}
        createProjectLoading={createProjectLoading}
        createProjectError={createProjectError}
      />
    </Box>
  )
}
